// One account's activity, line by line, in posting order. No running balance and no opening
// balance: the engine's account page serves neither, and Books never sums.

import { ChevronLeft } from 'lucide-react';
import { Button, Card, Icon } from '@givmo/design-system';
import type { BooksHome } from './books-wire';
import type { BooksWords } from './books-words';
import { BooksLoading, BooksPageShell, BooksStateCard } from './books-ui';
import { BooksLink } from './books-links';
import { useBooksAccount, useShowMore, type BooksWalk } from './books-queries';
import { refusalOf } from './books-state';
import { count, dateShort, moneyOrDash } from './books-format';
import { EntryMeta, WALK_WORDS, dimensionWords } from './ledger-page';

const HEAD =
  'h-[40px] px-16 font-sans text-s11 font-w600 uppercase tracking-wide text-text-secondary';

export function AccountPage({
  home,
  words,
  accountCode,
}: {
  home: BooksHome;
  words: BooksWords;
  accountCode: string;
}) {
  const account = home.chart.find((candidate) => candidate.account_code === accountCode);
  const back = (
    <BooksLink
      view={{ page: 'ledger' }}
      className="inline-flex items-center gap-4 font-sans text-s13 font-w600 tracking-givmo text-brand-600 hover:underline"
      data-testid="books-account-back"
    >
      <Icon icon={ChevronLeft} size={14} aria-hidden />
      Chart of accounts
    </BooksLink>
  );
  if (account === undefined) {
    return (
      <BooksPageShell title="Ledger" testId="books-account">
        {back}
        <BooksStateCard
          testId="books-account-not-found"
          title="These books’ chart has no account with that code"
        />
      </BooksPageShell>
    );
  }
  return (
    <BooksPageShell
      title={`${account.account_code} · ${account.account_name}`}
      description={`${words.accountType(account.account_type)} · ${words.section(account.section, account.account_type)}`}
      testId="books-account"
    >
      <div className="-mt-12">{back}</div>
      <AccountLines home={home} words={words} accountCode={accountCode} walk="oldest_first" />
    </BooksPageShell>
  );
}

function AccountLines({
  home,
  words,
  accountCode,
  walk,
}: {
  home: BooksHome;
  words: BooksWords;
  accountCode: string;
  walk: BooksWalk;
}) {
  const activity = useBooksAccount(home.org_ref, accountCode, walk);
  const { showMore, restarting } = useShowMore(activity);
  if (activity.isPending) return <BooksLoading>Loading the account…</BooksLoading>;
  if (activity.isError && !restarting) {
    return refusalOf(activity.error).code === 'BOOKS_ACCOUNT_NOT_FOUND' ? (
      <BooksStateCard
        testId="books-account-not-found"
        title="These books’ chart has no account with that code"
      />
    ) : (
      <BooksStateCard
        testId="books-account-unavailable"
        title="This account can’t be read right now"
        action={
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => void activity.refetch()}
            testId="books-account-retry"
          >
            Try again
          </Button>
        }
      />
    );
  }
  // Restarting a walk whose cursor was refused keeps the lines already shown on screen.
  const lines = (activity.data?.pages ?? []).flatMap((page) => page.lines);
  if (lines.length === 0) {
    return <BooksStateCard testId="books-account-empty" title="No activity on this account yet" />;
  }
  return (
    <>
      <p className="font-sans text-s13 tracking-givmo text-text-secondary">
        Every line posted to this account, {WALK_WORDS[walk].toLowerCase()}. Showing{' '}
        {count(lines.length)} {lines.length === 1 ? 'line' : 'lines'}.
      </p>
      <Card testId="books-account-lines">
        <table className="w-full border-collapse text-left">
          <colgroup>
            <col style={{ width: '128px' }} />
            <col />
            <col style={{ width: '150px' }} />
            <col style={{ width: '150px' }} />
          </colgroup>
          <thead className="bg-neutral-50">
            <tr className="border-b border-border-subtle">
              <th className={HEAD}>Date</th>
              <th className={HEAD}>Entry</th>
              <th className={`${HEAD} text-right`}>Debit</th>
              <th className={`${HEAD} text-right`}>Credit</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr
                key={`${line.journal_entry_id}-${line.line_no}`}
                className="border-b border-border-subtle last:border-b-0"
              >
                <td className="h-[48px] px-16 pt-12 align-top font-sans text-s13 tabular-nums tracking-givmo text-text-secondary">
                  {dateShort(line.effective_date)}
                </td>
                <td className="px-16 py-10">
                  <div className="flex flex-col gap-2">
                    <span className="font-sans text-s14 tracking-givmo text-text">{line.memo}</span>
                    <span className="flex flex-wrap items-center gap-8">
                      <EntryMeta
                        words={words}
                        kind={line.entry_kind}
                        source={line.source_event_id}
                      />
                      {line.dimension.length > 0 ? (
                        <span className="font-sans text-s12 tracking-givmo text-text-tertiary">
                          {dimensionWords(words, line.dimension)}
                        </span>
                      ) : null}
                    </span>
                  </div>
                </td>
                <td className="px-16 py-10 pt-12 text-right align-top font-sans text-s14 tabular-nums tracking-givmo text-text">
                  {line.debit_cents ? moneyOrDash(line.debit_cents) : ''}
                </td>
                <td className="px-16 py-10 pt-12 text-right align-top font-sans text-s14 tabular-nums tracking-givmo text-text">
                  {line.credit_cents ? moneyOrDash(line.credit_cents) : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {activity.hasNextPage ? (
        <div className="flex justify-center">
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => void showMore()}
            disabled={activity.isFetchingNextPage || restarting}
            testId="books-account-more"
          >
            {activity.isFetchingNextPage || restarting ? 'Loading…' : 'Show more lines'}
          </Button>
        </div>
      ) : null}
    </>
  );
}
